import { useContext } from 'react';
import { motion } from 'framer-motion';
import { EndpointExtended } from '@context/openapi/types';
import APICard from '../APICard';
import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { useExplorer } from '@/hooks/useExplorer';
import { explorerAnalytics } from '@/lib/analytics';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { plural } from 'pluralize';
import { cn } from '@components/lib/utils';

interface APIRecommendationProps {
  endpoints: EndpointExtended[];
}

const APIRecommendations: React.FC<APIRecommendationProps> = ({
  endpoints,
}) => {
  const { selectedSDK } = useExplorer();
  const { isLoggedIn } = useContext(ClientAuthContext);
  const { isGenerating } = useCodeGenerationStore();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 10,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        damping: 15,
        stiffness: 80,
      },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="api-recommendations-container"
    >
      {(endpoints as EndpointExtended[]).map((endpoint, index) => {
        const endpointPath = endpoint.id.replace(
          `-${endpoint.method.toLowerCase()}`,
          ''
        );
        const method = endpoint.method.toUpperCase();
        const actionURL = `${plural(endpoint.resourceId)}/${endpoint.id}?sdk=${selectedSDK}`;
        const description = [
          endpoint.shortDescription,
          endpoint.description,
        ].join('. ');

        return (
          <motion.div
            key={index}
            variants={cardVariants}
            className={cn('api-card-wrapper', {
              'pointer-events-none': isGenerating,
            })}
            onClick={() => {
              explorerAnalytics.aiRecommendationClicked({
                resource: endpoint.resourceId || '',
                endpoint: endpoint.id || '',
                httpMethod: endpoint.method || '',
                recommendationSource: 'ai_chat',
                isLoggedIn: !!isLoggedIn,
              });
            }}
          >
            <APICard
              endpoint={endpointPath}
              method={method}
              name={endpoint.name}
              actionURL={actionURL}
              description={description}
              actionLabel="View in Playground"
              resource={endpoint.resourceId || ''}
            />
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default APIRecommendations;
